import { Injectable } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { forkJoin, from, lastValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take, tap, timeout } from 'rxjs/operators';
import { Animal, AnimalSummary } from './animal';
import { isLivingAnimal, sliceAnimalPage } from './animal-living';
import { BaseIndexedDbRepository } from 'libs/arch/src/lib/repositories/indexed-db/indexdb-repository';
import { SynchronizationService } from 'libs/arch/src/lib/repositories/indexed-db/synchronization.service';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import {
    Actives,
    ApiService,
    Filter,
    MultipartPayload,
    NetworkStatusService,
    PageData,
    SortMode,
    SortOrder
} from '@homeleite/arch';
import { normalizeUpperCase } from '@homeleite/common-utils';
import { base64ToBlob, fileToBase64 } from '../../../../common-utils/src/lib/utils/file.utils';
import { Gender } from './gender';

@Injectable({ providedIn: 'root' })
export class AnimalsRepository extends BaseIndexedDbRepository<AnimalSummary, Animal> {

    /**
     * Cache dos vivos por produtor (1 request sync → fatias no cliente).
     * Antes: count/page compartilhavam fetch paginado (size 20 no server) e a lista parava em ~20.
     */
    private readonly livingByProducer = new Map<string, Observable<Animal[]>>();

    protected mapToEntity(dbItem: Animal): Animal {
        return Animal.fromJson(dbItem);
    }

    protected readonly storeName: string = 'animals';

    protected getResourcePath(): string {
        return 'animals';
    }

    protected getSearchableFields(): string[] {
        return ['name', 'register', 'nickname', 'apcId', 'electronicBottom'];
    }

    protected mapToSummary(animal: Animal): AnimalSummary {
        if (!animal) return null;
        return new AnimalSummary(
            animal.id,
            animal.apcId,
            animal.name,
            animal.register,
            animal.nickname,
            animal.birthdate,
            animal.imageUrl,
            animal.imageId,
            animal.mother,
            animal.numberOfChildren,
            isLivingAnimal(animal)
        );
    }

    private onlyInHerd<T extends Animal | AnimalSummary>(animals: T[]): T[] {
        return (animals ?? []).filter((a) => isLivingAnimal(a));
    }

    constructor(
        dbService: NgxIndexedDBService,
        synchronizationService: SynchronizationService,
        private networkStatus: NetworkStatusService,
        private apiService: ApiService
    ) {
        super(dbService, synchronizationService);
        this.registerAnimalReferences('motherId');
    }

    /** Invalida cache de vivos (após sync manual / CRUD). */
    public invalidateLivingCache(producerId?: string): void {
        if (producerId) {
            this.livingByProducer.delete(producerId);
        } else {
            this.livingByProducer.clear();
        }
    }

    override insert(animal: Animal): Observable<Animal> {
        if (animal) {
            animal.nickname = normalizeUpperCase(animal.nickname);
            this.invalidateLivingCache(animal.producer?.id);
        }

        return super.insert(animal);
    }

    override update(animal: Animal): Observable<Animal> {
        if (animal) {
            animal.nickname = normalizeUpperCase(animal.nickname);
            this.invalidateLivingCache(animal.producer?.id);
        }

        return super.update(animal);
    }

    override count(query?: string, filters?: Filter[], actives?: Actives): Observable<number> {
        const producerId = this.getProducerIdFromFilters(filters);
        if (!producerId) return of(0);

        return this.livingListSource(producerId).pipe(
            map((list) => this.prepareLivingList(list, query).length),
            catchError(() => super.count(query, filters, actives))
        );
    }

    override page(
        pageData: PageData,
        query?: string,
        filters?: Filter[],
        sortOrders?: SortOrder[],
        actives?: Actives
    ): Observable<AnimalSummary[]> {
        const producerId = this.getProducerIdFromFilters(filters);
        if (!producerId) return of([]);

        // Local-first: não rebaixa o sync inteiro a cada volta à lista (page 0).
        // Cache em memória / IDB; rede só se ainda não houver dados (ou invalidateLivingCache).
        const source$ = this.livingListSource(producerId);

        return source$.pipe(
            map((list) => {
                const prepared = this.prepareLivingList(list, query, sortOrders);
                return sliceAnimalPage(prepared, pageData).map((item) => this.mapToSummary(item));
            }),
            catchError(() =>
                super.page(pageData, query, filters, sortOrders, actives).pipe(
                    map((list) => this.onlyInHerd(list as any).map((item) => this.mapToSummary(item as any)))
                )
            )
        );
    }

    override list(
        query?: string,
        filters?: Filter[],
        sortOrders?: SortOrder[],
        actives?: Actives
    ): Observable<AnimalSummary[]> {
        const producerId = this.getProducerIdFromFilters(filters);
        if (!producerId) return of([]);

        return this.livingListSource(producerId).pipe(
            map((list) =>
                this.prepareLivingList(list, query, sortOrders).map((item) => this.mapToSummary(item))
            ),
            catchError(() => super.list(query, filters, sortOrders, actives))
        );
    }

    override listFull(
        query?: string,
        filters?: Filter[],
        sortOrders?: SortOrder[],
        actives?: Actives
    ): Observable<Animal[]> {
        const producerId = this.getProducerIdFromFilters(filters);
        if (!producerId) return of([]);

        return this.livingListSource(producerId).pipe(
            map((list) => this.prepareLivingList(list, query, sortOrders)),
            catchError(() => super.listFull(query, filters, sortOrders, actives))
        );
    }

    override pageFull(
        pageData: PageData,
        query?: string,
        filters?: Filter[],
        sortOrders?: SortOrder[],
        actives?: Actives
    ): Observable<Animal[]> {
        const producerId = this.getProducerIdFromFilters(filters);

        if (!producerId) return of([]);

        const source$ = this.livingListSource(producerId);

        return source$.pipe(
            map((list) =>
                sliceAnimalPage(this.prepareLivingList(list, query, sortOrders), pageData)
            ),
            catchError(() => super.pageFull(pageData, query, filters, sortOrders, actives))
        );
    }

    public existsInDb(id: string): Observable<boolean> {
        return super.find(id).pipe(map((result) => !!result));
    }

    public listFemalesBy(producerId: string, filterName?: string): Observable<AnimalSummary[]> {
        const getFromDb = () => {
            return this.dbService.getAll<Animal>(this.storeName).pipe(
                map((animals) => {
                    const producerAnimals = animals.filter(
                        (animal) =>
                            animal.producer?.id === producerId &&
                            animal.isActive === true &&
                            String(animal.gender) === Gender.FEMALE.name
                    );

                    if (!filterName || !filterName.trim()) return producerAnimals;

                    const searchTerm = filterName.toLowerCase().trim();

                    return producerAnimals.filter(
                        (animal) =>
                            animal.nickname && animal.nickname.toLowerCase().includes(searchTerm)
                    );
                }),
                map((filteredAnimals) =>
                    filteredAnimals.map((animal) => this.mapToSummary(animal))
                ),
                take(1),
                catchError(() => of([]))
            );
        };

        if (!this.networkStatus.isOnline()) return getFromDb();

        let params = new HttpParams();

        if (filterName) params = params.set('filterName', filterName);
        params = params.set('gender', Gender.FEMALE.name)

        return this.apiService.root
            .path(this.getResourcePath())
            .path('producer')
            .path(producerId)
            .getJson<AnimalSummary[]>({ params })
            .pipe(
                map((response) => response || []),
                take(1),
                catchError(() => getFromDb())
            );
    }

    public findByNickname(producerId: string, nickname: string, gender?: Gender): Observable<AnimalSummary | null> {
        if (!nickname || !nickname.trim()) return of(null);

        const targetNickname = normalizeUpperCase(nickname);

        const getFromDb = () => {

            return this.dbService.getAll<Animal>(this.storeName).pipe(
                map((animals) => {

                    return animals.find(
                        (animal) =>
                            animal.producer?.id === producerId &&
                            normalizeUpperCase(animal.nickname) === targetNickname &&
                            animal.isActive === true &&
                            (!gender || String(animal.gender) === gender.name)
                    );
                }),
                map((foundAnimal) => (foundAnimal ? this.mapToSummary(foundAnimal) : null)),
                catchError(() => of(null))
            );
        };

        if (!this.networkStatus.isOnline()) return getFromDb();

        let params = new HttpParams()
            .set(
                'nickname',
                targetNickname
            )

        if (gender) {
            params = params.set(
                'gender',
                gender.name
            );
        }

        return this.apiService.root
            .path(this.getResourcePath())
            .path('producer')
            .path(producerId)
            .path('nickname')
            .getJson<AnimalSummary>({ params })
            .pipe(
                map((response) => response),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 404) return of(null);
                    return getFromDb();
                })
            );
    }

    public insertWithPicture(animal: Animal, imageFile: File): Observable<Animal> {
        if (animal) {
            animal.nickname = normalizeUpperCase(animal.nickname);
        }

        const processRequest = async () => {
            const imageRecord = {
                animalId: animal.id,
                imageData: imageFile,
                timestamp: new Date().toISOString()
            };

            await lastValueFrom(
                forkJoin({
                    savedAnimal: this.dbService.update(this.storeName, animal),
                    savedImage: this.dbService.update('animalImages', imageRecord)
                })
            );

            const base64Data = await fileToBase64(imageFile);

            const animalPayload = {
                ...animal,
                gender: animal.gender?.name,
                variety: animal.variety?.description,
                mother: animal.mother ? { id: animal.mother.id } : null,
                father: animal.father ? { id: animal.father.id } : null
            };

            const multipartPayload: MultipartPayload = {
                filePart: {
                    base64Data: base64Data,
                    filename: imageFile.name
                },
                jsonPart: animalPayload
            };

            this.synchronizationService
                .attemptSync<MultipartPayload>(
                    {
                        method: 'POST',
                        resourcePath: `${this.getResourcePath()}/with-picture`,
                        payload: multipartPayload,
                        isMultipart: true
                    },
                    this.storeName
                )
                .subscribe({
                    error: (err) =>
                        console.error('Falha ao enfileirar criação de animal com imagem.', err)
                });

            return animal;
        };

        return from(processRequest());
    }

    /** IDs sem foto (404) nesta sessão — evita martelar a API na lista. */
    private readonly missingPictureIds = new Set<string>();

    /**
     * Busca thumb/medium. Sem `imageId` conhecido, não vai à rede (lista rápida + ícone padrão).
     * Blob vazio/não-imagem no IDB é ignorado (era a causa de “foto quebrada”).
     */
    public getImageBy(
        animalId: string,
        size: 'thumb' | 'medium' = 'thumb',
        opts?: { imageId?: string | null; skipNetworkIfNoImageId?: boolean }
    ): Observable<Blob> {
        if (!animalId) {
            return throwError(() => new Error('animalId obrigatório'));
        }
        if (this.missingPictureIds.has(animalId)) {
            return throwError(() => new Error('Sem foto'));
        }
        const skipNet =
            opts?.skipNetworkIfNoImageId !== false &&
            opts?.imageId !== undefined &&
            !opts.imageId;

        return this.dbService
            .getByKey<{ imageData: Blob; size?: string }>('animalImages', animalId)
            .pipe(
                switchMap((record) => {
                    const cached = AnimalsRepository.usableImageBlob(record?.imageData)
                        ? record.imageData
                        : undefined;
                    const satisfies =
                        cached && (size === 'thumb' || record.size !== 'thumb');
                    if (satisfies) {
                        return of(cached);
                    }
                    if (skipNet || !this.networkStatus.isOnline()) {
                        return cached
                            ? of(cached)
                            : throwError(() => new Error('Sem foto'));
                    }
                    return this.fetchImage(animalId, size, cached);
                })
            );
    }

    private static usableImageBlob(blob?: Blob | null): boolean {
        if (!blob || blob.size < 32) {
            return false;
        }
        const type = (blob.type || '').toLowerCase();
        return !type || type.startsWith('image/');
    }

    /** Limita downloads paralelos — em 3G vários thumbs ao mesmo tempo travam a lista. */
    private imageFetchInFlight = 0;
    private readonly imageFetchWaiters: Array<() => void> = [];
    private static readonly MAX_PARALLEL_IMAGE_FETCHES = 2;

    private acquireImageSlot(): Observable<void> {
        if (this.imageFetchInFlight < AnimalsRepository.MAX_PARALLEL_IMAGE_FETCHES) {
            this.imageFetchInFlight++;
            return of(undefined);
        }
        return new Observable<void>((subscriber) => {
            this.imageFetchWaiters.push(() => {
                this.imageFetchInFlight++;
                subscriber.next();
                subscriber.complete();
            });
        });
    }

    private releaseImageSlot(): void {
        this.imageFetchInFlight = Math.max(0, this.imageFetchInFlight - 1);
        const next = this.imageFetchWaiters.shift();
        if (next) {
            next();
        }
    }

    private fetchImage(animalId: string, size: 'thumb' | 'medium', cached?: Blob): Observable<Blob> {
        const ms = size === 'thumb' ? 4000 : 15000;
        return this.acquireImageSlot().pipe(
            switchMap(() =>
                this.apiService.root
                    .path('animals', animalId, 'picture')
                    .getBlob({ params: new HttpParams().set('size', size) })
                    .pipe(
                        timeout(ms),
                        switchMap((blob) => {
                            if (!AnimalsRepository.usableImageBlob(blob)) {
                                this.missingPictureIds.add(animalId);
                                return throwError(() => new Error('Foto inválida'));
                            }
                            return this.dbService
                                .update('animalImages', {
                                    animalId,
                                    imageData: blob,
                                    size,
                                    timestamp: new Date().toISOString()
                                })
                                .pipe(
                                    map(() => blob),
                                    catchError(() => of(blob))
                                );
                        }),
                        catchError((err) => {
                            const status = err?.status ?? err?.name;
                            if (status === 404 || status === 408 || err?.name === 'TimeoutError') {
                                this.missingPictureIds.add(animalId);
                            }
                            return cached ? of(cached) : throwError(() => err);
                        }),
                        finalize(() => this.releaseImageSlot())
                    )
            )
        );
    }

    protected getProperty(obj: any, path: string) {
        return path
            .split('.')
            .reduce((o, key) => (o && o[key] !== 'undefined' ? o[key] : null), obj);
    }

    protected applySorting(list: Animal[], sortOrders?: SortOrder[]): Animal[] {
        if (!sortOrders || sortOrders.length === 0) {
            return list;
        }

        return [...list].sort((a, b) => {
            for (const order of sortOrders) {
                const valA = this.getProperty(a, order.field);
                const valB = this.getProperty(b, order.field);

                const comparison = valA < valB ? -1 : valA > valB ? 1 : 0;

                if (comparison !== 0) {
                    return order.sortMode === SortMode.DSC ? -comparison : comparison;
                }
            }
            return 0;
        });
    }

    private getProducerIdFromFilters(filters?: Filter[]): string | undefined {
        const producerFilter = filters?.find(
            (filter) => filter.field === 'producer.$id' || filter.field === 'producer.id'
        );
        return producerFilter?.value;
    }

    /**
     * Prefere cache/IDB; só busca rede se a lista local estiver vazia.
     * Evita rebaixar ~16MB do sync a cada abertura / volta da ficha do animal.
     */
    private livingListSource(producerId: string): Observable<Animal[]> {
        const cached = this.livingByProducer.get(producerId);
        if (cached) {
            return cached;
        }

        if (!this.networkStatus.isOnline()) {
            return this.livingFromLocalDb(producerId);
        }

        return this.livingFromLocalDb(producerId).pipe(
            switchMap((local) => {
                if (local.length > 0) {
                    // Aquece cache em memória e, em background, tenta refresh de rede
                    // sem bloquear a UI (volta da ficha / reentrada na lista).
                    const warm$ = of(local).pipe(shareReplay({ bufferSize: 1, refCount: false }));
                    this.livingByProducer.set(producerId, warm$);
                    this.refreshLivingInBackground(producerId);
                    return warm$;
                }
                return this.loadAllLiving(producerId);
            }),
            catchError(() => this.loadAllLiving(producerId))
        );
    }

    /** Refresh de rede sem limpar a lista já exibida. */
    private refreshLivingInBackground(producerId: string): void {
        this.apiService.root
            .path(this.getResourcePath(), 'producer', 'sync', producerId)
            .getJson<any[]>()
            .pipe(
                map((response) =>
                    (Array.isArray(response) ? response : [])
                        .map((item) => Animal.fromJson(item))
                        .filter((animal) => animal && isLivingAnimal(animal))
                ),
                tap((animals) => {
                    this.livingByProducer.set(
                        producerId,
                        of(animals).pipe(shareReplay({ bufferSize: 1, refCount: false }))
                    );
                    this.persistLivingInBackground(animals);
                }),
                catchError(() => of([]))
            )
            .subscribe();
    }

    /**
     * Todos os vivos do produtor em 1 request (`/animals/producer/sync/:id`).
     * Paginação/count/list só fatiam em memória — alta performance e lista completa.
     */
    private loadAllLiving(producerId: string, force = false): Observable<Animal[]> {
        if (force) {
            this.livingByProducer.delete(producerId);
        }
        const cached = this.livingByProducer.get(producerId);
        if (cached) {
            return cached;
        }

        const request$ = this.apiService.root
            .path(this.getResourcePath(), 'producer', 'sync', producerId)
            .getJson<any[]>()
            .pipe(
                // Rebanho grande pode demorar; sem teto a lista fica em spinner eterno.
                timeout(120_000),
                map((response) =>
                    (Array.isArray(response) ? response : [])
                        .map((item) => Animal.fromJson(item))
                        .filter((animal) => animal && isLivingAnimal(animal))
                ),
                tap((animals) => this.persistLivingInBackground(animals)),
                shareReplay({ bufferSize: 1, refCount: false }),
                catchError((err) => {
                    this.livingByProducer.delete(producerId);
                    return throwError(() => err);
                })
            );

        this.livingByProducer.set(producerId, request$);
        return request$;
    }

    private livingFromLocalDb(producerId: string): Observable<Animal[]> {
        return this.dbService.getAll<Animal>(this.storeName).pipe(
            map((animals) =>
                (animals ?? [])
                    .map((item) => this.mapToEntity(item))
                    .filter(
                        (animal) => animal?.producer?.id === producerId && isLivingAnimal(animal)
                    )
            )
        );
    }

    private prepareLivingList(
        list: Animal[],
        query?: string,
        sortOrders?: SortOrder[]
    ): Animal[] {
        const living = (list ?? []).filter((animal) => isLivingAnimal(animal));
        const sorted = this.applySorting(living, sortOrders);
        return this.applyTextFilter(sorted, query);
    }

    private persistLivingInBackground(animals: Animal[]): void {
        if (!animals?.length) {
            return;
        }

        const withInlineImages = animals.filter((animal) => {
            const { auditData } = animal;
            return (
                (auditData?.createdByIntegration ||
                    (auditData?.createdByApp && auditData?.modifiedByIntegration)) &&
                animal.imageUrl?.startsWith('data:image')
            );
        });

        if (withInlineImages.length > 0) {
            const imageRecords = withInlineImages.map((animal) => {
                const imageBlob = base64ToBlob(animal.imageUrl);
                delete animal.imageUrl;
                return {
                    animalId: animal.id,
                    imageData: imageBlob,
                    timestamp: new Date().toISOString()
                };
            });
            this.dbService.bulkAdd('animalImages', imageRecords).subscribe({
                error: (err) => console.error('Erro ao salvar imagens:', err)
            });
        }

        this.saveLocally(animals).subscribe({
            error: (err) => console.error('Erro ao salvar animais:', err)
        });
    }

    private applyTextFilter(list: Animal[], query?: string): Animal[] {
        if (!query || !query.trim()) return list;
        const lowerCaseQuery = query.toLowerCase().trim();
        return list.filter(
            (animal) =>
                animal.nickname?.toLowerCase().includes(lowerCaseQuery) ||
                animal.name?.toLowerCase().includes(lowerCaseQuery) ||
                animal.register?.toLowerCase().includes(lowerCaseQuery)
        );
    }
}
